// Chips de celda de las tablas del backoffice: el relleno (`.chip` del mock) para estados y el
// contorno (`.bloq-pill`) para lecturas secundarias. Los dos toman el color de un tono.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type PillTone = 'success' | 'danger' | 'warning' | 'info' | 'neutral';

/** Relleno + borde del chip, como `.chip-*` del mock: los dos llevan el color del tono. */
export const STATUS_PILL_TONE_CLASS: Record<PillTone, string> = {
    success: 'border-success-soft-border bg-success-soft text-success',
    danger: 'border-danger-soft-border bg-danger-soft text-danger',
    warning: 'border-warning-soft-border bg-warning-soft text-warning',
    info: 'border-info-soft-border bg-info-soft text-info',
    neutral: 'border-border bg-muted text-muted-foreground',
};

/** Contorno de una pastilla por tono (`.bloq-pill` del mock): borde y texto del color, sin relleno. */
export const TONE_OUTLINE_CLASS: Record<PillTone, string> = {
    success: 'border-success text-success',
    danger: 'border-danger text-danger',
    warning: 'border-warning text-warning',
    info: 'border-info text-info',
    neutral: 'border-muted-foreground text-muted-foreground',
};

export interface StatusPillProps {
    tone: PillTone;
    title?: string;
    children: ReactNode;
    className?: string;
}

/** Chip relleno de celda (`.chip` del mock a 9px). */
export function StatusPill({ tone, title, children, className }: StatusPillProps) {
    return (
        <span
            title={title}
            className={cn(
                'inline-flex items-center whitespace-nowrap rounded-full border px-[5px] py-px text-[9px] font-medium',
                STATUS_PILL_TONE_CLASS[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

export interface OutlinePillProps {
    /** Clases de borde y texto; normalmente `TONE_OUTLINE_CLASS[tone]`. */
    outline: string;
    title?: string;
    children: ReactNode;
    className?: string;
}

/** Pastilla en contorno (`.bloq-pill` del mock). */
export function OutlinePill({ outline, title, children, className }: OutlinePillProps) {
    return (
        <span
            title={title}
            className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-full border bg-card px-1.5 py-[2px] text-[9.5px] font-semibold',
                outline,
                className,
            )}
        >
            {children}
        </span>
    );
}
